import { CardCourse } from 'components';
import { Link, useLocation } from 'react-router-dom';

import { CoursesInterface } from 'types';

import './style.scss';
import { useEffect, useMemo, useState } from 'react';
import { useCourses } from 'contexts';

export type BoxCoursesProps = {
  courses: CoursesInterface[];
  title: string;
  maxLength?: number;
  children?: React.ReactNode;
  className?: string;
  classNameCards?: string;
  style?: React.CSSProperties;
  [key: string]: any;
  profiles?: string[];
};

type TabFilter = 'all' | 'in-progress' | 'completed';

export function BoxCourses({
  courses,
  title,
  maxLength,
  children,
  className,
  classNameCards,
  style,
  profiles,
  ...rest
}: BoxCoursesProps) {
  const location = useLocation();
  const { allProgress } = useCourses();
  const [filteredCourses, setFilteredCourses] = useState<CoursesInterface[]>([]);
  const [activeTab, setActiveTab] = useState<TabFilter>('all');

  useEffect(() => {
    if (profiles && profiles.length > 0) {
      const normalizedProfiles = profiles.map(el => el.toLowerCase());

      const updatedCourses = courses.filter(course => {
        if (course.extra && course.extra.teams && course.extra.teams.length > 0) {
          return course.extra.teams.some(team => normalizedProfiles.includes(team.replace(/\s/g, '').toLowerCase()));
        } else {
          return course;
        }
      });

      setFilteredCourses(updatedCourses);
    } else {
      setFilteredCourses(courses);
    }
  }, [courses, profiles]);

  const getPercent = (courseId: string): number => {
    if (allProgress?.length) {
      const coursePercent = allProgress.find(item => item._id === courseId);
      if (coursePercent) return coursePercent.percent;
    }
    return 0;
  };

  const displayedCourses = useMemo(() => {
    let list = filteredCourses;

    if (activeTab === 'in-progress') {
      list = filteredCourses.filter(c => {
        const p = getPercent(c._id);
        return p > 0 && p < 100;
      });
    } else if (activeTab === 'completed') {
      list = filteredCourses.filter(c => getPercent(c._id) >= 100);
    }

    if (typeof maxLength === 'number') {
      return list.slice(0, maxLength);
    }

    return list;
    
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredCourses, activeTab, allProgress, maxLength]);

  const tabs: { label: string; value: TabFilter }[] = [
    { label: 'Todos', value: 'all' },
    { label: 'Em Andamento', value: 'in-progress' },
    { label: 'Concluídos', value: 'completed' },
  ];

  return (
    <section className={`${className || ''}`} style={style} {...rest}>
      {/* Header: Title + Tabs */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        {!!title?.length && (
          <h2 className="text-xl lg:text-2xl font-medium text-[#181b1a]">
            {title} <span className="text-orange">({filteredCourses ? filteredCourses.length : courses.length})</span>
          </h2>
        )}

        <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1">
          {tabs.map(tab => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${
                activeTab === tab.value
                  ? 'bg-white text-[#181b1a] shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {!!displayedCourses.length && (
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-5 ${classNameCards || ''}`}>
          {displayedCourses.map((course) => (
            <CardCourse key={course._id} course={course} />
          ))}
        </div>
      )}

      {!displayedCourses.length && (
        <p className="text-center text-gray-400 py-10">Nenhum curso encontrado nesta categoria.</p>
      )}

      {children}
    </section>
  );
}
